import { ColumnDef } from '@tanstack/react-table'
import { Tooltip } from 'antd'

import EllipsisText from '@/components/Atoms/EllipsisText/EllipsisText'
import OrderStatus from '@/components/Orders/OrderStatus/OrderStatus'
import { ORDER_STATUSES_IN_SELECT } from '@/constants'
import { OrderInterface, OrderStatusType } from '@/types'
import { trimTrailingZeros } from '@/utils/format/trimTrailingZeros'

const getFromStorage = <T,>(key: string, defaultValue: T): T => {
  const item = localStorage.getItem(key)
  if (!item) return defaultValue
  try {
    return JSON.parse(item) as T
  } catch (e) {
    console.error(`Error parsing localStorage key "${key}":`, e)
    return defaultValue
  }
}

export const modifyLocalStorageOrders = (
  ordersFromServer: OrderInterface['orderModel'][],
  userId: string
) => {
  if (!userId) return

  const userStoredOrdersStatuses = getFromStorage<
    Record<string, Record<number, OrderStatusType>>
  >('userStoredOrdersStatuses', {})

  const userUnviewedOrders = getFromStorage<Record<string, string[]>>(
    'userUnviewedOrders',
    {}
  )

  if (!userStoredOrdersStatuses[userId]) userStoredOrdersStatuses[userId] = {}
  if (!userUnviewedOrders[userId]) userUnviewedOrders[userId] = []

  let hasChanges = false

  ordersFromServer.forEach((order) => {
    const orderCode = order.code
    const serverStatus = order.orderStatus
    const localStatus = userStoredOrdersStatuses[userId][orderCode]

    if (localStatus && localStatus !== serverStatus) {
      const codeStr = String(orderCode)
      if (!userUnviewedOrders[userId].includes(codeStr)) {
        userUnviewedOrders[userId].push(codeStr)
        hasChanges = true
      }
    }

    if (localStatus !== serverStatus) {
      userStoredOrdersStatuses[userId][orderCode] = serverStatus
      hasChanges = true
    }
  })

  if (hasChanges) {
    localStorage.setItem(
      'userStoredOrdersStatuses',
      JSON.stringify(userStoredOrdersStatuses)
    )
    localStorage.setItem(
      'userUnviewedOrders',
      JSON.stringify(userUnviewedOrders)
    )
  }
}

export const statusOptions = [
  { label: 'Все статусы', value: 'ALL' },
  ...Object.entries(ORDER_STATUSES_IN_SELECT).map(([key, value]) => ({
    label: value,
    value: key,
  })),
]

export const isStatusFilter = (
  status: string
): status is OrderStatusType | 'ALL' =>
  statusOptions.some((option) => option.value === status)

export const ORDERS_COLUMN_WIDTHS: Partial<Record<string, string>> = {
  orderStatus: 'minmax(260px, 0.7fr)',
  code: '100px',
  createdAt: '120px',
  client: 'minmax(150px, 280px)',
  author: 'minmax(100px, 0.5fr)',
  totalPrice: '120px',
  contract: 'minmax(150px, 0.7fr)',
  deliveryAddress: 'minmax(150px, 0.7fr)',
  comment: 'minmax(200px, 300px)',
}

export const columns: ColumnDef<OrderInterface['orderModel'], unknown>[] = [
  {
    header: 'Статус',
    accessorKey: 'orderStatus',
    cell: (info) => <OrderStatus order={info.row.original} />,
  },
  {
    header: 'Код заказа',
    accessorKey: 'code',
    cell: (info) => <span>{info.row.original.code}</span>,
  },
  {
    header: 'Дата заказа',
    accessorKey: 'createdAt',
    cell: (info) => {
      const date = new Date(info.row.original.createdAt)
      const formattedDate = date.toLocaleDateString()
      const formattedTime = date.toLocaleTimeString()

      return (
        <Tooltip title={formattedTime}>
          <span>{formattedDate}</span>
        </Tooltip>
      )
    },
  },
  {
    header: 'Клиент',
    id: 'client',
    cell: (info) => {
      const record = info.row.original
      if (record.clientShortModel) {
        const clientName =
          record.clientShortModel.description ||
          record.clientShortModel.shortLegalName ||
          ''

        return <EllipsisText text={clientName} tooltipMinLength={15} />
      }
      return <span>-</span>
    },
  },
  {
    header: 'Автор',
    id: 'author',
    cell: (info) => {
      const record = info.row.original
      const authorText =
        record.authorShortModel?.description ??
        (record as { author?: string }).author ??
        '-'

      return <EllipsisText text={authorText} tooltipMinLength={15} />
    },
  },
  {
    header: 'Сумма, BYN',
    accessorKey: 'totalPrice',
    cell: (info) => {
      const val = info.row.original.totalPrice
      return (
        <span>{typeof val === 'number' ? trimTrailingZeros(val) : '0'}</span>
      )
    },
  },
  {
    header: 'Договор',
    id: 'contract',
    cell: (info) => {
      const contract = info.row.original.contractShortModel?.description ?? '-'

      return <EllipsisText text={contract} tooltipMinLength={15} />
    },
  },
  {
    header: 'Адрес доставки',
    accessorKey: 'deliveryAddress',
    cell: (info) => {
      const addr = info.row.original.deliveryAddress
      if (!addr) {
        return <span>Самовывоз</span>
      }

      return <EllipsisText text={addr} tooltipMinLength={25} />
    },
  },
  {
    header: 'Комментарий',
    accessorKey: 'comment',
    cell: (info) => {
      const comment = info.row.original.comment
      if (!comment) return <span></span>

      return <EllipsisText text={comment} tooltipMinLength={20} />
    },
  },
]
